/**
 * Keyboard inset: how many px at the bottom of the layout viewport the on-screen keyboard covers, so anything
 * pinned to the bottom (a sheet footer, a composer) can ride above it and a focused field can stay visible.
 *
 * One shared source for every surface:
 * - Phone chat owns the keyboard (iOS, `nativeChatKeyboard.ts` switches resize to None): its
 *   `chatKeyboardHeight` is the inset.
 * - Native otherwise: `@capacitor/keyboard` keyboardWillShow / WillHide (plus DidShow / DidHide, which Android
 *   fires with the final height). A keyboard the WebView shrinks for is not an inset: the shrink alone makes
 *   room. That's always the case on Android (the system bars pad the WebView by the IME) and on iOS while the
 *   resize mode is Native (`nativeResizeMode.ts`). Reporting it there would lift a sheet by the keyboard at
 *   keyboardWillShow and then again when iOS shrinks the WebView ~450ms later, then drop it back. Only iOS
 *   with resize None (the auth screen) reports the plugin's height, unless the WebView shrank anyway.
 * - Web: `visualViewport` (innerHeight - vv.height - vv.offsetTop), only while a text field has focus and the
 *   page isn't pinch-zoomed.
 *
 * `height` is the inset to apply; `visible` is whether a keyboard is on screen at all.
 */
import { Capacitor } from '@capacitor/core'
import { computed, ref, watch, type ComputedRef } from 'vue'
import { chatKeyboardHeight } from '@/utils/nativeChatKeyboard'
import { nativeResizeMode, type NativeResizeMode } from '@/utils/nativeResizeMode'
import { getNativePlatform, isNativePlatform } from '@/utils/platform'
import { prefersReducedMotion } from '@/motion/tokens'

/** A WebView that shrank this much already made room for the keyboard. */
const WEBVIEW_RESIZED_PX = 80
/** Smaller visualViewport differences are browser chrome (URL bar), not a keyboard. */
const MIN_WEB_KEYBOARD_PX = 80

const nativeKeyboard = ref(0)
const shrunk = ref(0)
const webInset = ref(0)
let baseHeight = 0
let baseWidth = 0
let started = false

/**
 * The inset a native keyboard of `keyboard` px leaves for the page to handle: 0 when the WebView shrinks (or
 * already shrank, by `shrunk` px) around it.
 */
export function nativeKeyboardInset(keyboard: number, opts: { platform: 'ios' | 'android' | 'web'; mode: NativeResizeMode; shrunk: number }): number {
    if (keyboard <= 0) return 0
    if (opts.platform === 'android') return 0
    if (opts.platform === 'ios' && opts.mode === 'native') return 0
    return opts.shrunk > WEBVIEW_RESIZED_PX ? 0 : keyboard
}

const height = computed(() => {
    if (chatKeyboardHeight.value > 0) return chatKeyboardHeight.value
    if (nativeKeyboard.value > 0) {
        return nativeKeyboardInset(nativeKeyboard.value, { platform: getNativePlatform(), mode: nativeResizeMode.value, shrunk: shrunk.value })
    }
    return webInset.value
})
const visible = computed(() => chatKeyboardHeight.value > 0 || nativeKeyboard.value > 0 || webInset.value > 0)

const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'radio', 'range', 'color', 'file', 'submit', 'reset', 'image', 'hidden'])

/** Does focusing this element bring up a keyboard (or the iOS picker that takes its place)? */
export function isTextEntry(el: Element | null | undefined): el is HTMLElement {
    if (!el || !(el instanceof HTMLElement)) return false
    if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return !el.disabled
    if (el instanceof HTMLInputElement) return !el.disabled && !el.readOnly && !NON_TEXT_INPUTS.has(el.type)
    return el.isContentEditable
}

function measureShrink() {
    const h = window.innerHeight
    if (nativeKeyboard.value === 0 && chatKeyboardHeight.value === 0) {
        // No keyboard known yet. A resize that shrinks the WebView while a text field has focus is most likely
        // the keyboard arriving before its keyboardWillShow (Android can order them that way): keep the
        // taller base so the shrink still reads as one. A width change (rotation) always re-bases.
        const w = window.innerWidth
        if (w !== baseWidth || h >= baseHeight || !isTextEntry(document.activeElement)) baseHeight = h
        baseWidth = w
        shrunk.value = 0
    } else {
        shrunk.value = Math.max(0, baseHeight - h)
    }
}

async function startNative() {
    if (!Capacitor.isPluginAvailable('Keyboard')) return
    baseHeight = window.innerHeight
    baseWidth = window.innerWidth
    window.addEventListener('resize', measureShrink, { passive: true })
    try {
        const { Keyboard } = await import('@capacitor/keyboard')
        const show = (info: { keyboardHeight: number }) => {
            if (nativeKeyboard.value === 0) baseHeight = Math.max(baseHeight, window.innerHeight)
            nativeKeyboard.value = Math.max(0, Math.round(info.keyboardHeight || 0))
            measureShrink()
        }
        const hide = () => {
            nativeKeyboard.value = 0
            shrunk.value = 0
        }
        await Keyboard.addListener('keyboardWillShow', show)
        await Keyboard.addListener('keyboardDidShow', show)
        await Keyboard.addListener('keyboardWillHide', hide)
        await Keyboard.addListener('keyboardDidHide', () => {
            hide()
            // The keyboard is gone: whatever height the WebView has now is the base again.
            baseHeight = window.innerHeight
            baseWidth = window.innerWidth
        })
    } catch {
        // Keyboard plugin unavailable: the inset stays 0 (chat mode still reports its own height).
    }
}

function startWeb() {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
        if (!isTextEntry(document.activeElement) || vv.scale > 1.01) {
            webInset.value = 0
            return
        }
        const inset = window.innerHeight - vv.height - vv.offsetTop
        webInset.value = inset > MIN_WEB_KEYBOARD_PX ? Math.min(Math.round(inset), window.innerHeight) : 0
    }
    vv.addEventListener('resize', update, { passive: true })
    vv.addEventListener('scroll', update, { passive: true })
    document.addEventListener('focusin', update)
    document.addEventListener('focusout', () => setTimeout(update, 0))
}

function start() {
    if (started || typeof window === 'undefined') return
    started = true
    if (isNativePlatform()) void startNative()
    else startWeb()
}

export interface KeyboardInset {
    /** px of the layout viewport the keyboard covers (0 when the WebView shrinks around it instead). */
    height: ComputedRef<number>
    /** A keyboard is on screen. */
    visible: ComputedRef<boolean>
}

/** The shared keyboard inset. Cheap to call from any component; listeners install once. */
export function useKeyboardInset(): KeyboardInset {
    start()
    return { height, visible }
}

function scrollParent(el: HTMLElement): HTMLElement | null {
    for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
        const cs = getComputedStyle(n)
        if (/(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) return n
    }
    return null
}

/** Scroll `el` into the middle of what's visible above the keyboard, if it isn't already comfortably visible. */
export function revealAboveKeyboard(el: HTMLElement, opts: { behavior?: ScrollBehavior; margin?: number } = {}): void {
    const behavior = opts.behavior ?? (prefersReducedMotion() ? 'auto' : 'smooth')
    const margin = opts.margin ?? 16
    const viewBottom = window.innerHeight - height.value
    const r = el.getBoundingClientRect()
    const sp = scrollParent(el)
    if (!sp) {
        if (r.top < margin || r.bottom > viewBottom - margin) el.scrollIntoView({ block: 'center', behavior })
        return
    }
    const s = sp.getBoundingClientRect()
    const top = Math.max(s.top, 0) + margin
    const bottom = Math.min(s.bottom, viewBottom) - margin
    if (r.top >= top && r.bottom <= bottom) return
    // Tall fields (a long textarea) align their top; everything else centres.
    const delta = r.height > bottom - top ? r.top - top : r.top + r.height / 2 - (top + bottom) / 2
    sp.scrollBy({ top: delta, behavior })
}

/**
 * Keep a just-focused field visible: waits until the keyboard inset and the window size stop changing (the
 * keyboard finished animating, the sheet finished lifting, a native WebView shrink landed), then centres the
 * field in the space above the keyboard. Returns a cancel function.
 */
export function keepAboveKeyboard(el: HTMLElement, opts: { settleMs?: number; behavior?: ScrollBehavior } = {}): () => void {
    start()
    const settle = opts.settleMs ?? 320
    let timer: ReturnType<typeof setTimeout> | null = null
    let done = false
    const finish = () => {
        done = true
        if (timer) clearTimeout(timer)
        stop()
        window.removeEventListener('resize', arm)
    }
    const arm = () => {
        if (done) return
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
            finish()
            if (el.isConnected) revealAboveKeyboard(el, opts)
        }, settle)
    }
    const stop = watch(height, arm)
    window.addEventListener('resize', arm, { passive: true })
    arm()
    return finish
}
